<template>
  <div>
    <div class="content">
      <!-- Subscription Modal if needed -->
      <SubscriptionModal
        :show="showPaywall"
        :payment-failed="paymentFailed"
        @close="closePaywallModal"
        @select-plan="upgrade"
        @navigate-to-contact="navigateToContact"
        @navigate-to-trial="navigateToTrial"
        @navigate-to-student="navigateToStudent"
      />

      <div class="student-section text-center">
        <h2 class="search-title">{{ $t('student.titlePrefix') }} <span class="highlight">{{ $t('student.titleHighlight') }}</span></h2>
        <div class="main-section">
          <!-- Student Information Section -->
          <div class="form-container">
            <h3>{{ $t('student.form.heading') }}</h3>
            <p class="form-subtitle">{{ $t('student.form.subtitle') }}</p>
            <form @submit.prevent="submitForm" class="student-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="firstname">{{ $t('student.form.firstname') }}</label>
                  <input
                    type="text"
                    id="firstname"
                    v-model="formData.firstname"
                    class="form-control"
                    :class="{ 'is-invalid': errors.firstname }"
                    required
                  >
                  <div v-if="errors.firstname" class="error-message">{{ errors.firstname }}</div>
                </div>

                <div class="form-group">
                  <label for="lastname">{{ $t('student.form.lastname') }}</label>
                  <input
                    type="text"
                    id="lastname"
                    v-model="formData.lastname"
                    class="form-control"
                    :class="{ 'is-invalid': errors.lastname }"
                    required
                  >
                  <div v-if="errors.lastname" class="error-message">{{ errors.lastname }}</div>
                </div>
              </div>

              <div class="form-group">
                <label for="university">{{ $t('student.form.university') }}</label>
                <select
                  id="university"
                  v-model="formData.university"
                  class="form-control"
                  :class="{ 'is-invalid': errors.university }"
                  required
                  style="height: 50px;"
                >
                  <option value="" disabled selected>{{ $t('student.form.universityPlaceholder') }}</option>
                  <option v-for="uni in universities" :key="uni.id" :value="uni.id">
                    {{ uni.name }}
                  </option>
                  <option value="other">{{ $t('student.form.otherUniversityOption') }}</option>
                </select>
                <div v-if="errors.university" class="error-message">{{ errors.university }}</div>
              </div>

              <div class="form-group" v-if="formData.university === 'other'">
                <label for="otherUniversity">{{ $t('student.form.otherUniversityLabel') }}</label>
                <input
                  type="text"
                  id="otherUniversity"
                  v-model="formData.otherUniversity"
                  class="form-control"
                  :class="{ 'is-invalid': errors.otherUniversity }"
                  required
                  style="height: 50px;"
                >
                <div v-if="errors.otherUniversity" class="error-message">{{ errors.otherUniversity }}</div>
              </div>

              <div class="form-group">
                <label for="studyLevel">{{ $t('student.form.studyLevel') }}</label>
                <select
                  id="studyLevel"
                  v-model="formData.studyLevel"
                  class="form-control"
                  :class="{ 'is-invalid': errors.studyLevel }"
                  required
                  style="height: 50px;"
                >
                  <option value="" disabled selected>{{ $t('student.form.studyLevelPlaceholder') }}</option>
                  <option value="licence1">{{ $t('student.studyLevels.licence1') }}</option>
                  <option value="licence2">{{ $t('student.studyLevels.licence2') }}</option>
                  <option value="licence3">{{ $t('student.studyLevels.licence3') }}</option>
                  <option value="master1">{{ $t('student.studyLevels.master1') }}</option>
                  <option value="master2">{{ $t('student.studyLevels.master2') }}</option>
                  <option value="doctorat">{{ $t('student.studyLevels.doctorat') }}</option>
                  <option value="other">{{ $t('student.studyLevels.other') }}</option>
                </select>
                <div v-if="errors.studyLevel" class="error-message">{{ errors.studyLevel }}</div>
              </div>
              <div class="form-actions">
                <button type="button" @click="goBack" class="back-button">
                  {{ $t('common.back') }}
                </button>
                <button type="submit" class="btn-primary" :disabled="isSubmitting">
                  <span v-if="isSubmitting">
                    <div class="spinner"></div>
                    {{ $t('student.form.processing') }}
                  </span>
                  <span v-else>
                    {{ $t('student.form.subscribeCta') }}
                  </span>
                </button>
              </div>
            </form>
          </div>

          <div class="faq-section">
            <h3>{{ $t('student.faq.heading') }}</h3>

            <div class="faq-item">
              <div class="faq-question">{{ $t('student.faq.cancelQuestion') }}</div>
              <div class="faq-answer">{{ $t('student.faq.cancelAnswer') }}</div>
            </div>

            <div class="faq-item">
              <div class="faq-question">{{ $t('student.faq.durationQuestion') }}</div>
              <div class="faq-answer">{{ $t('student.faq.durationAnswer') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SubscriptionModal from './SubscriptionModal.vue';
import { getSubscription } from "@/services/api";
import { getCurrentUser } from "@/supabase/auth";

export default {
  name: 'StudentPage',

  components: {
    SubscriptionModal
  },

  data() {
    return {
      // Form data
      formData: {
        firstname: '',
        lastname: '',
        university: '',
        otherUniversity: '',
        studyLevel: '',
        termsAccepted: false
      },

      // Errors object
      errors: {
        firstname: '',
        lastname: '',
        university: '',
        otherUniversity: '',
        studyLevel: '',
        termsAccepted: ''
      },

      // Form state
      isSubmitting: false,
      showPaywall: false,
      paymentFailed: false,

      // Liste complète des universités de droit en France
      universities: [
        // Hauts-de-France
        { id: 'picardie', name: 'Université de Picardie – Jules Verne' },
        { id: 'littoral', name: "Université du Littoral Côte d'Opale" },
        { id: 'lille', name: 'Université de Lille' },
        { id: 'artois', name: "Université d'Artois" },
        { id: 'uphf', name: 'Université Polytechnique Hauts-de-France' },

        // Normandie
        { id: 'caen', name: 'Université de Caen Normandie' },
        { id: 'rouen', name: 'Université de Rouen' },
        { id: 'lehavre', name: 'Université Le Havre Normandie' },

        // Bretagne
        { id: 'brest', name: 'Université de Bretagne Occidentale' },
        { id: 'rennes1', name: 'Université de Rennes 1' },
        { id: 'ubs', name: 'Université de Bretagne Sud' },

        // Pays de la Loire
        { id: 'angers', name: "Université d'Angers" },
        { id: 'nantes', name: 'Université de Nantes' },
        { id: 'lemans', name: 'Le Mans Université' },

        // Centre-Val de Loire
        { id: 'tours', name: 'Université de Tours' },
        { id: 'orleans', name: "Université d'Orléans" },

        // Ile-de-France
        { id: 'cergy', name: 'CY Cergy Paris Université' },
        { id: 'upec', name: 'Université Paris-Est Créteil Val-de-Marne' },
        { id: 'evry', name: "Université d'Evry-Val d'Essonne" },
        { id: 'uvsq', name: 'Université de Versailles Saint-Quentin-en-Yvelines' },
        { id: 'paris', name: 'Université de Paris' },
        { id: 'paris2', name: 'Université Panthéon-Assas – Paris II' },
        { id: 'nanterre', name: 'Université Paris Nanterre' },
        { id: 'paris1', name: 'Université Paris I – Panthéon Sorbonne' },
        { id: 'paris8', name: 'Université Paris 8 – Vincennes – Saint-Denis' },
        { id: 'paris-saclay', name: 'Université Paris-Saclay' },
        { id: 'paris13', name: 'Université Sorbonne Paris Nord' },

        // Grand Est
        { id: 'lorraine', name: 'Université de Lorraine' },
        { id: 'uha', name: 'Université de Haute-Alsace' },
        { id: 'reims', name: 'Université de Reims Champagne Ardenne' },
        { id: 'strasbourg', name: 'Université de Strasbourg' },

        // Bourgogne-Franche-Comté
        { id: 'fcomte', name: 'Université de Franche-Comté' },
        { id: 'bourgogne', name: 'Université de Bourgogne' },

        // Nouvelle Aquitaine
        { id: 'poitiers', name: 'Université de Poitiers' },
        { id: 'uppa', name: "Université de Pau et des Pays de l'Adour" },
        { id: 'larochelle', name: 'La Rochelle Université' },
        { id: 'limoges', name: 'Université de Limoges' },
        { id: 'bordeaux', name: 'Université de Bordeaux' },

        // Occitanie
        { id: 'perpignan', name: 'Université de Perpignan – Via Domitia' },
        { id: 'champollion', name: 'Institut National Universitaire Jean-François Champollion' },
        { id: 'avignon', name: 'Avignon Université' },
        { id: 'nimes', name: 'Université de Nîmes' },
        { id: 'toulouse1', name: 'Université Toulouse 1 – Capitole' },
        { id: 'montpellier', name: 'Université de Montpellier' },

        // Auvergne Rhône-Alpes
        { id: 'lyon2', name: 'Université Lumière – Lyon 2' },
        { id: 'savoie', name: 'Université Savoie Mont Blanc' },
        { id: 'clermont', name: 'Université Clermont Auvergne' },
        { id: 'lyon3', name: 'Université Jean Moulin – Lyon 3' },
        { id: 'stetienne', name: 'Université Jean Monnet' },
        { id: 'grenoble', name: 'Université Grenoble Rhône-Alpes' },

        // Sud-Provence-Alpes-Côte d'Azur
        { id: 'amu', name: 'Aix-Marseille Université' },
        { id: 'toulon', name: 'Université de Toulon' },
        { id: 'nice', name: "Université Côte d'Azur" },

        // Corse et Outre-mer
        { id: 'corse', name: 'Université de Corse Pasquale Paoli' },
        { id: 'reunion', name: 'Université de La Réunion' },
        { id: 'antilles', name: 'Université des Antilles' },
        { id: 'mayotte', name: 'Centre universitaire de Mayotte' },
        { id: 'guyane', name: 'Université de Guyane' },
        { id: 'nouvelle-caledonie', name: 'Université de la Nouvelle-Calédonie' },
        { id: 'polynesie', name: 'Université de la Polynésie Française' },

        // Autres établissements
        { id: 'sciences-po', name: 'Sciences Po Paris' },
        { id: 'other', name: 'Autre (préciser)' }
      ]
    };
  },

  mounted() {
    // Check if user is already subscribed
    this.checkSubscriptionStatus();
  },

  methods: {
    async checkSubscriptionStatus() {
      try {
        const sub = await getSubscription();
        // If user already has a student plan, redirect to dashboard
        if (sub.plan === "student" && sub.status === "active") {
          this.$toast.info(this.$t('student.toasts.alreadySubscribed'));
          this.$router.push('/dashboardPage');
        }
      } catch (error) {
        console.error("Error checking subscription:", error);
      }
    },

    goBack() {
      this.$router.push('/dashboardPage');
    },

    showTerms() {
      // Show terms and conditions modal or redirect to terms page
      this.$router.push('/conditionsGeneralePage');
    },

    validateForm() {
      // Reset all errors
      for (const key in this.errors) {
        this.errors[key] = '';
      }

      let isValid = true;

      // Validate firstname
      if (!this.formData.firstname.trim()) {
        this.errors.firstname = this.$t('student.errors.firstnameRequired');
        isValid = false;
      }

      // Validate lastname
      if (!this.formData.lastname.trim()) {
        this.errors.lastname = this.$t('student.errors.lastnameRequired');
        isValid = false;
      }

      // Validate university
      if (!this.formData.university) {
        this.errors.university = this.$t('student.errors.universityRequired');
        isValid = false;
      }

      // Validate other university if selected
      if (this.formData.university === 'other' && !this.formData.otherUniversity.trim()) {
        this.errors.otherUniversity = this.$t('student.errors.otherUniversityRequired');
        isValid = false;
      }

      // Validate study level
      if (!this.formData.studyLevel) {
        this.errors.studyLevel = this.$t('student.errors.studyLevelRequired');
        isValid = false;
      }


      return isValid;
    },

    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      try {
        const user = await getCurrentUser();

        if (!user) {
          throw new Error('User not authenticated');
        }

        // Proceed to Stripe checkout for student plan
        this.proceedToCheckout();

      } catch (error) {
        console.error('Error submitting student verification:', error);
        this.$toast.error(this.$t('student.toasts.submitError'));
      } finally {
        this.isSubmitting = false;
      }
    },

    proceedToCheckout() {
      // Self-checkout retiré : la conversion passe désormais par un conseiller (sales-led).
      // On redirige vers la page de contact au lieu d'ouvrir Stripe Checkout.
      this.$router.push('/contactPage');
    },

    // Modal related methods from DashboardPage
    closePaywallModal() {
      this.showPaywall = false;
    },

    upgrade() {
      // Self-checkout retiré : la conversion passe désormais par un conseiller (sales-led).
      // On redirige vers la page de contact au lieu d'ouvrir Stripe Checkout.
      this.navigateToContact();
    },

    navigateToTrial() {
      this.showPaywall = false;
      this.$router.push('/trialPage');
    },

    navigateToStudent() {
      this.showPaywall = false;
      // Already on student page, no need to navigate
    },

    navigateToContact() {
      this.showPaywall = false;
      this.$router.push('/contactPage');
    }
  }
};
</script>

<style scoped>
.content {
  padding: 20px;
  font-family: 'Roboto', sans-serif;
  background-color: #F5F6FA;
  min-height: 100vh;
}

.student-section {
  margin-bottom: 20px;
  margin-top: 20px;
}

.search-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 30px;
  color: #333;
}

.highlight {
  color: #625CFB;
}

.main-section {
  width: 80%;
  max-width: 900px;
  margin: 0 auto;
}

/* Info box styling */
.info-box {
  background: linear-gradient(to right, #E9EFF4, #E2FAFFCC);
  border-radius: 12px;
  padding: 30px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

.info-content h3 {
  font-size: 22px;
  color: #333;
  margin-bottom: 30px;
  text-align: center;
  font-weight: 600;
}

.benefits-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
}

.benefit-card {
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
  background-color: white;
  padding: 25px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s, box-shadow 0.3s;
}

.benefit-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.benefit-icon {
  background-color: #f0f4ff;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.icon {
  width: 28px;
  height: 28px;
  color: #6c63ff;
}

.benefit-text {
  text-align: center;
}

.benefit-text h4 {
  font-size: 18px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 600;
}

.benefit-text p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.price-banner {
  background-color: #6c63ff;
  color: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  margin-top: 20px;
  box-shadow: 0 6px 16px rgba(108, 99, 255, 0.3);
}

.price-content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.price-value {
  font-size: 36px;
  font-weight: bold;
  margin-right: 15px;
}

.price-period {
  font-size: 18px;
  font-weight: normal;
}

.price-regular {
  font-size: 16px;
  opacity: 0.8;
  text-decoration: line-through;
  margin-left: 10px;
}

/* Form container styling */
.form-container {
  margin-top: 30px;
  background-color: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

.form-container h3 {
  font-size: 22px;
  color: #333;
  margin-bottom: 10px;
  text-align: center;
  font-weight: 600;
}

.form-subtitle {
  font-size: 16px;
  color: #666;
  text-align: center;
  margin-bottom: 30px;
}

/* Use Cases Section */
.use-cases-container {
  display: flex;
  justify-content: space-between;
  margin: 30px 0;
  flex-wrap: wrap;
  gap: 20px;
}

.use-case-card {
  flex: 1;
  min-width: 220px;
  padding: 25px;
  background-color: #f8f9fa;
  border-radius: 12px;
  text-align: center;
  border-left: 4px solid #6c63ff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.use-case-icon {
  font-size: 32px;
  margin-bottom: 15px;
}

.use-case-card h4 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.use-case-card p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* Testimonials */
.testimonials-section {
  margin-top: 40px;
}

.testimonials-container {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  flex-wrap: wrap;
  gap: 20px;
}

.testimonial-card {
  flex: 1;
  min-width: 280px;
  padding: 25px;
  background-color: #f5f7ff;
  border-radius: 12px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.testimonial-card:before {
  content: '"';
  position: absolute;
  top: 10px;
  left: 15px;
  font-size: 60px;
  color: rgba(108, 99, 255, 0.1);
  font-family: Georgia, serif;
}

.testimonial-quote {
  font-style: italic;
  margin-bottom: 15px;
  color: #333;
  position: relative;
  z-index: 2;
  padding-left: 15px;
}

.testimonial-author {
  font-weight: 600;
  color: #6c63ff;
  text-align: right;
}

/* FAQ Section */
.faq-section {
  margin-top: 40px;
  background-color: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.01),
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 6px 12px rgba(0, 0, 0, 0.05);
}

.faq-section h3 {
  text-align: center;
  font-size: 22px;
  margin-bottom: 20px;
  font-weight: 600;
}

.faq-item {
  border-bottom: 1px solid #eee;
  padding: 15px 0;
}

.faq-item:last-child {
  border-bottom: none;
}

.faq-question {
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 16px;
}

.faq-answer {
  color: #666;
  font-size: 14px;
}

/* Form Styling */
.student-form {
  max-width: 700px;
  margin: 30px auto 0;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
}

.form-row .form-group {
  flex: 1;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #444;
  font-size: 15px;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #6c63ff;
  box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.1);
}

.form-control.is-invalid {
  border-color: #ff5252;
}

.error-message {
  color: #ff5252;
  font-size: 13px;
  margin-top: 5px;
}

.form-text {
  font-size: 12px;
  color: #777;
  margin-top: 5px;
}

.form-checks {
  margin-bottom: 25px;
}

.form-check {
  display: flex;
  align-items: center;
}

.form-check-input {
  margin-right: 10px;
}

.form-check-label {
  font-size: 14px;
}

.form-check-label a {
  color: #6c63ff;
  text-decoration: underline;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.back-button {
  background-color: white;
  color: #6c63ff;
  border: 1px solid #6c63ff;
  padding: 12px 25px;
  border-radius: 25px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f6f5ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.btn-primary {
  background-color: #6c63ff;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.25);
}

.btn-primary:hover {
  background-color: #5a54d1;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(108, 99, 255, 0.35);
}

.btn-primary:disabled {
  background-color: #a8a4ea;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Spinner styles */
.spinner {
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .main-section {
    width: 95%;
  }

  .benefits-container,
  .use-cases-container,
  .testimonials-container {
    flex-direction: column;
  }

  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .form-actions {
    flex-direction: column;
    gap: 15px;
  }

  .back-button {
    order: 2;
  }

  .btn-primary {
    order: 1;
  }
}
</style>
